import { FC, useCallback, useEffect, useState } from "react";

import Stack from "@mui/material/Stack";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardAdaptiveModal } from "@/dashboard-design-system/AdaptiveModal";
import { DashboardCheckbox } from "@/dashboard-design-system/DashboardCheckbox/DashboardCheckbox";
import { DashboardTypography } from "@/dashboard-design-system/Typography";

type EndAuctionConfirmationDialogProps = {
  open?: boolean;
  onClose?: () => void;
  onConfirm?: () => Promise<void>;
};

export const EndAuctionConfirmationDialog: FC<EndAuctionConfirmationDialogProps> = ({
  open = false,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslate();
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (open) {
      setIsAcknowledged(false);
      setShowError(false);
    }
  }, [open]);

  const handleConfirm = useCallback(async () => {
    if (!isAcknowledged) {
      setShowError(true);
      return;
    }
    await onConfirm?.();
  }, [isAcknowledged, onConfirm]);

  return (
    <DashboardAdaptiveModal
      isOpen={open}
      onClose={onClose}
      title={
        <DashboardTypography variant="h6">{t("campaignHub", "endAuctionConfirmationDialog.title")}</DashboardTypography>
      }
      actions={{
        main: {
          vibe: "warning",
          children: t("campaignHub", "endAuctionConfirmationDialog.cta.confirm"),
          onClick: handleConfirm,
        },
        cancel: {
          vibe: "neutral",
          children: t("campaignHub", "endAuctionConfirmationDialog.cta.cancel"),
          onClick: onClose,
        },
      }}
      noDivider
    >
      <Stack spacing={2} width="100%">
        <DashboardTypography variant="body2">
          {t("campaignHub", "endAuctionConfirmationDialog.body")}
        </DashboardTypography>
        <DashboardCheckbox
          checked={isAcknowledged}
          onChange={(_event, checked) => {
            setIsAcknowledged(checked);
            if (checked) {
              setShowError(false);
            }
          }}
          label={t("campaignHub", "endAuctionConfirmationDialog.acknowledgement")}
          error={showError}
          errorMessage={showError ? t("campaignHub", "endAuctionConfirmationDialog.error") : undefined}
          data-test="end-auction-acknowledgement"
        />
      </Stack>
    </DashboardAdaptiveModal>
  );
};
